<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useAuthentication } from '@/shared/composables/useAuthentication';

/**
 * Le rappel permanent qu'on n'est pas soi-même.
 *
 * La console ouvre un onglet séparé, ce qui limite déjà la confusion — mais un onglet se
 * retrouve, se recharge et se met en favori. Le bandeau reste affiché tant que la session
 * dure, en haut de l'application et devant tout le reste, et il porte sa propre sortie :
 * quitter une session usurpée ne doit pas demander de savoir comment.
 */
const { t } = useI18n();
const { isImpersonating, impersonatedEmail, stopImpersonation } =
  useAuthentication();
</script>

<template>
  <div
    v-if="isImpersonating"
    class="impersonation"
    role="alert"
    aria-live="polite"
    data-testid="impersonation-banner"
  >
    <span class="impersonation__label">
      {{ t('impersonation.viewingAs') }}
      <strong>{{ impersonatedEmail }}</strong>
    </span>
    <button
      class="impersonation__exit"
      type="button"
      data-testid="impersonation-exit"
      @click="stopImpersonation"
    >
      {{ t('impersonation.exit') }}
    </button>
  </div>
</template>

<style scoped>
.impersonation {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  /* Une couleur qui n'est celle d'aucun état du produit : le bandeau ne doit ressembler
     ni à une erreur, ni à une réussite, ni au reste du chrome. */
  background: #6d28d9;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.impersonation__label {
  flex: 1 1 auto;
  min-width: 0;
}

.impersonation__exit {
  flex: 0 0 auto;
  padding: 3px 12px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.impersonation__exit:hover {
  background: rgba(255, 255, 255, 0.16);
}
</style>
